import { useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { getGetSupportTicketCountsQueryKey, useGetSupportTicket } from '@/api/control/control-plane/control-plane';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { AttachmentPicker } from '@/components/support/AttachmentPicker';
import { ConsultantHoursCard } from '@/components/support/ConsultantHoursCard';
import { SupportAttachmentList } from '@/components/support/SupportAttachmentList';
import { SupportConversation } from '@/components/support/SupportConversation';
import { SupportStatusBadge } from '@/components/support/SupportStatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSupportAttachments } from '@/hooks/useSupportAttachments';
import {
  RESOLUTION_CATEGORY_LABELS,
  fillSupportTemplate,
  formatSupportDate,
  getDateLocale,
  getListedAttachments,
  getScanPollInterval,
  getSupportPersonLabel,
  getSupportTicketState,
  usesConsultantHours,
} from '@/lib/support';
import type { NewTicketNavigationState } from '@/pages/support/NewSupportTicketPage';

interface DetailRowProps {
  label: string;
  children: ReactNode;
}

/**
 * One label and value row in the ticket's Details panel.
 */
function DetailRow({ label, children }: DetailRowProps) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/**
 * A support ticket: the description at the top and the conversation below it, with the ticket's
 * details, Data Wrangler hours (for Data Wrangler work) and attachments alongside. Refetches after
 * posting or uploading, and polls while an attachment is being scanned so its status updates
 * without a page refresh.
 */
export default function SupportTicketDetailPage() {
  const { orgId, ticketId } = useParams<{ orgId: string; ticketId: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();
  const locale = getDateLocale(language);
  const { policy, validate, upload, hint } = useSupportAttachments(orgId ?? '');

  const { data, isLoading, error, refetch } = useGetSupportTicket(orgId ?? '', ticketId ?? '', {
    query: {
      enabled: !!orgId && !!ticketId,
      refetchInterval: (query) =>
        getScanPollInterval(query.state.data?.data.attachments, query.state.dataUpdateCount),
    },
  });
  const ticket = data?.data;

  const initialFailures = (location.state as NewTicketNavigationState | null)?.failedUploads ?? [];
  const [uploadErrors, setUploadErrors] = useState<string[]>(
    initialFailures.length > 0
      ? [fillSupportTemplate(t.support.newTicket.uploadsFailed, { files: initialFailures.join(', ') })]
      : []
  );
  const [isUploading, setIsUploading] = useState(false);

  if (!orgId || !ticketId) {
    return null;
  }

  const supportUrl = `/dashboard/orgs/${orgId}/support`;

  /**
   * Validates and uploads picked files straight away, then refetches the ticket.
   */
  const handlePick = async (picked: File[]) => {
    const { accepted, errors } = validate(picked);
    setUploadErrors(errors);
    if (accepted.length === 0) return;

    setIsUploading(true);
    try {
      const { failed } = await upload(ticketId, accepted);
      if (failed.length > 0) {
        setUploadErrors((current) => [
          ...current,
          ...failed.map((f) => fillSupportTemplate(t.support.detail.uploadFailed, { name: f.name })),
        ]);
      }
      if (failed.length < accepted.length) {
        toast.success(t.support.detail.uploaded);
      }
      await refetch();
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Refreshes the ticket and the ticket counts after a reply, since replying can hand the ticket
   * back to support.
   */
  const handlePosted = async () => {
    await Promise.all([
      refetch(),
      queryClient.invalidateQueries({ queryKey: getGetSupportTicketCountsQueryKey(orgId) }),
    ]);
  };

  const state = ticket ? getSupportTicketState(ticket) : undefined;

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to={supportUrl}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t.support.detail.back}
        </Link>
      </Button>

      {error ? (
        <QueryErrorState error={error} message={t.support.detail.loadFailed} onRetry={() => { void refetch(); }} />
      ) : isLoading || !ticket ? (
        <div className="space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight break-words">{ticket.title}</h1>
              <SupportStatusBadge ticket={ticket} />
            </div>
            <p className="text-sm text-muted-foreground">
              {fillSupportTemplate(t.support.tickets.openedBy, {
                name: getSupportPersonLabel(ticket.submitter, t.support.person),
              })}
              {' · '}
              {formatSupportDate(ticket.createdAt, locale)}
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="min-w-0 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t.support.detail.descriptionTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{ticket.description}</p>
                </CardContent>
              </Card>

              <SupportConversation orgId={orgId} ticket={ticket} onPosted={handlePosted} />
            </div>

            <aside className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t.support.detail.detailsTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-3">
                    <DetailRow label={t.support.detail.statusLabel}>
                      <div className="space-y-1">
                        <SupportStatusBadge ticket={ticket} />
                        {state && (
                          <p className="text-xs text-muted-foreground">{t.support.status[`${state}Tooltip`]}</p>
                        )}
                      </div>
                    </DetailRow>
                    {ticket.category && (
                      <DetailRow label={t.support.detail.type}>{t.support.tickets.types[ticket.category]}</DetailRow>
                    )}
                    <DetailRow label={t.support.detail.resolutionCategory}>
                      {ticket.resolutionCategory ? (
                        RESOLUTION_CATEGORY_LABELS[ticket.resolutionCategory]
                      ) : (
                        <span className="text-muted-foreground">{t.support.detail.notCategorized}</span>
                      )}
                    </DetailRow>
                    <DetailRow label={t.support.detail.openedBy}>
                      {getSupportPersonLabel(ticket.submitter, t.support.person)}
                    </DetailRow>
                    <DetailRow label={t.support.detail.created}>{formatSupportDate(ticket.createdAt, locale)}</DetailRow>
                    <DetailRow label={t.support.detail.updated}>
                      {formatSupportDate(ticket.modifiedAt ?? ticket.createdAt, locale)}
                    </DetailRow>
                    {ticket.resolvedAt && (
                      <DetailRow label={t.support.detail.resolved}>
                        {formatSupportDate(ticket.resolvedAt, locale)}
                      </DetailRow>
                    )}
                  </dl>
                </CardContent>
              </Card>

              {usesConsultantHours(ticket) && <ConsultantHoursCard orgId={orgId} />}

              <Card>
                <CardHeader>
                  <CardTitle>{t.support.detail.attachmentsTitle}</CardTitle>
                  <CardAction>
                    <AttachmentPicker
                      files={[]}
                      accept={policy?.allowedExtensions}
                      buttonLabel={isUploading ? t.support.detail.uploading : t.support.detail.addFiles}
                      disabled={isUploading}
                      onPick={(picked) => { void handlePick(picked); }}
                    />
                  </CardAction>
                </CardHeader>
                <CardContent className="space-y-3">
                  {uploadErrors.length > 0 && (
                    <Alert variant="destructive">
                      <AlertDescription>
                        {uploadErrors.map((message) => (
                          <div key={message}>{message}</div>
                        ))}
                      </AlertDescription>
                    </Alert>
                  )}
                  {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
                  <SupportAttachmentList
                    orgId={orgId}
                    ticketId={ticketId}
                    attachments={getListedAttachments(ticket.attachments)}
                  />
                </CardContent>
              </Card>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
